import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Modal,
  ModalOverlay,
  Tooltip,
  useToast,
  Table,
  Tr,
  Tbody,
  Td,
  useDisclosure,
  VStack,
  Skeleton,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { PaymentInKindServicingFeeDetailsGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { dollarNumberWithCommasOrDash } from 'packages/common-frontend/utils';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { getLoanLabel } from 'packages/invoices/ui/utils';
import { useAdjustCurrentLiabilityMutation } from 'packages/reporting/requests/reporting.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
  GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
} from 'packages/transactions/requests/transactions.gql.generated';

const PostServicerFeePaymentModal = ({
  isOpen,
  onClose,
  creditFacilityName,
  creditFacilityId,
  capitalProviderOrganizationName,
  loanLabels,
  totalAmount,
  accrualDates,
}: {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityName: string;
  creditFacilityId: string;
  capitalProviderOrganizationName: string;
  loanLabels: string[];
  totalAmount: number;
  accrualDates: {
    startDate: Util.FullDateStr;
    endDate: Util.FullDateStr;
  };
}) => {
  const toast = useToast();
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [
    selectedPaymentDate,
    setSelectedPaymentDate,
  ] = useState<Util.FullDateStr>(accrualDates.endDate);

  const [
    adjustCurrentLiability,
    { loading: isAdjustCurrentLiabilityLoading },
  ] = useAdjustCurrentLiabilityMutation({
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Servicer fee payment posted successfully.',
      });
    },
    refetchQueries: [
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
      GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
    ],
  });

  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({
    variables: {
      creditFacilityId,
    },
  });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return <Skeleton height="200px" />;
  }

  const adminAgentOrganization =
    creditFacilityData.creditFacility.capitalProviderRepresentativeOrganization;

  if (!adminAgentOrganization) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Post Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Typography>No admin agent found for selected facility.</Typography>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Post Payment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table borderWidth="1px" borderColor="gray.100" mt="24px">
            <Tbody>
              <Tr>
                <Td fontWeight="bold" py="0.5rem" px="1rem">
                  Accrual Dates
                </Td>
                <Td textAlign="right" color="gray.500" py="0.5rem" px="1rem">
                  {commonDate.fullDateStrToHumanDateString(
                    accrualDates.startDate,
                  )}{' '}
                  -{' '}
                  {commonDate.fullDateStrToHumanDateString(
                    accrualDates.endDate,
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" py="0.5rem" px="1rem">
                  Credit Facility
                </Td>
                <Td textAlign="right" color="gray.500" py="0.5rem" px="1rem">
                  {creditFacilityName}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" py="0.5rem" px="1rem">
                  Lender
                </Td>
                <Td textAlign="right" color="gray.500" py="0.5rem" px="1rem">
                  {capitalProviderOrganizationName}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" py="0.5rem" px="1rem">
                  Loans
                </Td>
                <Td textAlign="right" color="gray.500" py="0.5rem" px="1rem">
                  {loanLabels.map((label) => (
                    <Box key={label}>{label}</Box>
                  ))}
                </Td>
              </Tr>
              <Tr backgroundColor="aurora.50">
                <Td fontWeight="bold" py="0.5rem" px="1rem">
                  Total Accrued Servicer Fees
                </Td>
                <Td fontWeight="bold" textAlign="right" py="0.5rem" px="1rem">
                  {dollarNumberWithCommasOrDash(totalAmount)}
                </Td>
              </Tr>
            </Tbody>
          </Table>
          <VStack mt="24px" gap="16px" alignItems="flex-start">
            <Typography fontWeight="bold">Effective Date</Typography>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(selectedPaymentDate)}
              setSelectedDate={(date: Date | null) => {
                // date must exist. this datepicker is not clearable
                if (date) {
                  const newPaymentDate = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  setSelectedPaymentDate(newPaymentDate);
                }
              }}
              width="100%"
              dateFormat="MMMM d, yyyy"
            />
          </VStack>
        </ModalBody>

        <ModalFooter mt="24px">
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              try {
                await adjustCurrentLiability({
                  variables: {
                    amount: totalAmount,
                    counterpartyOrganizationId: adminAgentOrganization.id,
                    creditFacilityId,
                    currentLiabilityAccount: 'accruedAdminAgentSkimFee',
                    date: selectedPaymentDate,
                    type: 'decreaseCurrentLiability',
                    comment: `Admin agent skim fee payment for [Facility: ${creditFacilityName}] [Lender: ${capitalProviderOrganizationName}] [Admin agent: ${adminAgentOrganization.name}] [Accrual dates: ${accrualDates.startDate} to ${accrualDates.endDate}]`,
                  },
                });

                onClose();
              } catch (error) {
                toast({
                  ...errorToastConfig,
                  title: 'Failed to post servicer fee payment.',
                });
              }
            }}
            isLoading={isAdjustCurrentLiabilityLoading}
          >
            Post Payment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const PostServicerFeePaymentButton = ({
  servicingFeeDetails,
  accrualDates,
}: {
  servicingFeeDetails: PaymentInKindServicingFeeDetailsGQLType[];
  accrualDates: {
    startDate: Util.FullDateStr;
    endDate: Util.FullDateStr;
  };
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const totalAmount = useMemo(() => {
    const totalServicingFees = servicingFeeDetails.reduce(
      (acc, row) => acc + row.serviceFeeAmount,
      0,
    );

    return commonUtils.jsRound({
      value: totalServicingFees,
      precision: 2,
    });
  }, [servicingFeeDetails]);

  const uniqueCreditFacilityNames = useMemo(() => {
    return [
      ...new Set(servicingFeeDetails.map((row) => row.creditFacilityName)),
    ];
  }, [servicingFeeDetails]);

  const uniqueCapitalProviderNames = useMemo(() => {
    return [
      ...new Set(servicingFeeDetails.map((row) => row.capitalProviderName)),
    ];
  }, [servicingFeeDetails]);

  const uniqueLoanLabels = useMemo(() => {
    const loanLabels = servicingFeeDetails.map((row) =>
      getLoanLabel({
        loanId: row.capitalizedLoanId,
        loanName: row.capitalizedLoanName,
        externalLoanId: row.capitalizedExternalLoanId ?? null,
      }),
    );
    return [...new Set(loanLabels)];
  }, [servicingFeeDetails]);

  const isTotalAmountGreaterThanZero = totalAmount > 0;
  const isSingleFacilityAndSingleLenderSelected =
    uniqueCreditFacilityNames.length === 1 &&
    uniqueCapitalProviderNames.length === 1;

  const getTooltipLabel = () => {
    if (!isSingleFacilityAndSingleLenderSelected) {
      return 'Filter to a single facility and lender. Payment can only be posted for one lender at a time.';
    }

    if (!isTotalAmountGreaterThanZero) {
      return 'Total accrued servicer fees must be greater than zero to post payment.';
    }

    return undefined;
  };

  return (
    <>
      <Tooltip label={getTooltipLabel()} placement="bottom-start">
        <Box>
          <Button
            colorScheme="v2-aurora"
            variant="secondary"
            isDisabled={
              !isSingleFacilityAndSingleLenderSelected ||
              !isTotalAmountGreaterThanZero
            }
            onClick={onOpen}
          >
            Post Payment
          </Button>
        </Box>
      </Tooltip>
      {isSingleFacilityAndSingleLenderSelected && (
        <PostServicerFeePaymentModal
          isOpen={isOpen}
          onClose={onClose}
          creditFacilityName={uniqueCreditFacilityNames[0]}
          creditFacilityId={servicingFeeDetails[0].creditFacilityId}
          capitalProviderOrganizationName={uniqueCapitalProviderNames[0]}
          loanLabels={uniqueLoanLabels}
          totalAmount={totalAmount}
          accrualDates={accrualDates}
        />
      )}
    </>
  );
};
